import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import membershipService, {
  MembershipInfo,
  UsageHistory,
  RecordUsageRequest,
  SubscriptionStatus,
} from '../services/membershipService';
import { useAuth } from './AuthContext';

interface MembershipContextType {
  membershipInfo: MembershipInfo | null;
  subscriptionStatus: SubscriptionStatus | null;
  usageHistory: UsageHistory | null;
  loading: boolean;
  error: string | null;
  refreshMembership: () => Promise<void>;
  recordUsage: (usageData: RecordUsageRequest) => Promise<boolean>;
  getTierName: (tier: number) => string;
  getRemainingMinutes: () => number;
  hasMinutesRemaining: (requiredMinutes?: number) => boolean;
}

const MembershipContext = createContext<MembershipContextType | undefined>(
  undefined
);

export const useMembership = () => {
  const context = useContext(MembershipContext);
  if (context === undefined) {
    throw new Error('useMembership must be used within a MembershipProvider');
  }
  return context;
};

interface MembershipProviderProps {
  children: ReactNode;
}

export const MembershipProvider: React.FC<MembershipProviderProps> = ({
  children,
}) => {
  const [membershipInfo, setMembershipInfo] = useState<MembershipInfo | null>(
    null
  );
  const [subscriptionStatus, setSubscriptionStatus] =
    useState<SubscriptionStatus | null>(null);
  const [usageHistory, setUsageHistory] = useState<UsageHistory | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user, loading: authLoading } = useAuth();

  const refreshMembership = useCallback(async () => {
    if (!user || authLoading) return;

    try {
      setLoading(true);
      setError(null);

      // Fetch membership info
      try {
        const membershipData = await membershipService.getMembershipInfo();
        setMembershipInfo(membershipData);
      } catch (err) {
        console.error('Failed to fetch membership info:', err);
        setError(
          err instanceof Error ? err.message : 'Failed to fetch membership info'
        );
      }

      // Fetch subscription status
      try {
        const statusData = await membershipService.getSubscriptionStatus();
        setSubscriptionStatus(statusData);
      } catch (err) {
        console.error('Failed to fetch subscription status:', err);
        // Don't set error for subscription status failure
      }

      // Fetch usage history
      try {
        const historyData = await membershipService.getUsageHistory();
        setUsageHistory(historyData);
      } catch (err) {
        console.error('Failed to fetch usage history:', err);
        // Don't set error for usage history failure, as membership info is more critical
      }
    } catch (err) {
      console.error('Failed to refresh membership:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to refresh membership'
      );
    } finally {
      setLoading(false);
    }
  }, [user, authLoading]);

  const recordUsage = async (
    usageData: RecordUsageRequest
  ): Promise<boolean> => {
    try {
      const result = await membershipService.recordUsage(usageData);

      // Update membership info with new remaining minutes
      if (membershipInfo) {
        setMembershipInfo({
          ...membershipInfo,
          remaining_minutes: result.remaining_minutes,
          used_minutes:
            membershipInfo.monthly_minutes - result.remaining_minutes,
        });
      }

      // Refresh usage history to get the latest record
      try {
        const historyData = await membershipService.getUsageHistory();
        setUsageHistory(historyData);
      } catch (err) {
        console.error('Failed to refresh usage history after recording:', err);
      }

      return true;
    } catch (err) {
      console.error('Failed to record usage:', err);
      setError(err instanceof Error ? err.message : 'Failed to record usage');
      return false;
    }
  };

  const getTierName = (tier: number): string => {
    return membershipService.getTierName(tier);
  };

  const getRemainingMinutes = (): number => {
    return membershipInfo?.remaining_minutes || 0;
  };

  const hasMinutesRemaining = (requiredMinutes: number = 1): boolean => {
    return getRemainingMinutes() >= requiredMinutes;
  };

  // Load membership data when user changes
  useEffect(() => {
    if (user && !authLoading) {
      refreshMembership();
    } else if (!user && !authLoading) {
      // Clear membership data when user logs out
      setMembershipInfo(null);
      setSubscriptionStatus(null);
      setUsageHistory(null);
      setError(null);
    }
  }, [user, authLoading, refreshMembership]);

  const value: MembershipContextType = {
    membershipInfo,
    subscriptionStatus,
    usageHistory,
    loading,
    error,
    refreshMembership,
    recordUsage,
    getTierName,
    getRemainingMinutes,
    hasMinutesRemaining,
  };

  return (
    <MembershipContext.Provider value={value}>
      {children}
    </MembershipContext.Provider>
  );
};
